/**
 * Haptik (U15d): spürbare Rückmeldung beim Scannen. Kette: das Capacitor-Plugin „Haptics“ (nur in der App und nur,
 * wenn die Hülle es enthält; unter iOS die einzige Möglichkeit) → `navigator.vibrate` → nichts. Die Web-App bindet das
 * Plugin nicht als Paket ein, sondern spricht es über die Plugin-Brücke an: Der Web-Build läuft ohne Capacitor, und
 * ältere App-Versionen ohne Plugin fallen leise auf die Vibration zurück.
 * Hinweis: Wird später `@capacitor/haptics` direkt importiert, muss `registerPlugin` hier entfallen (doppelte Registrierung).
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

type ImpactStyle = 'LIGHT' | 'MEDIUM' | 'HEAVY';
type NotificationType = 'SUCCESS' | 'WARNING' | 'ERROR';

interface HapticsPlugin {
  impact(options: { style: ImpactStyle }): Promise<void>;
  notification(options: { type: NotificationType }): Promise<void>;
}

let plugin: HapticsPlugin | null | undefined;

/** Liefert die Plugin-Brücke, falls die native Hülle das Plugin enthält; sonst `null`. Das Ergebnis wird gemerkt. */
function nativeHaptics(): HapticsPlugin | null {
  if (plugin !== undefined) return plugin;
  try {
    plugin = Capacitor.isPluginAvailable('Haptics')
      ? registerPlugin<HapticsPlugin>('Haptics')
      : null;
  } catch {
    plugin = null;
  }
  return plugin;
}

/** Nur für Tests: gemerktes Plugin vergessen. */
export function resetHapticsForTest(): void {
  plugin = undefined;
}

function vibrate(pattern: number | number[]): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch {
    // Vibration nicht verfügbar
  }
}

/** Führt den nativen Aufruf aus; scheitert er (auch asynchron), greift die Vibration. */
function viaPlugin(
  call: (haptics: HapticsPlugin) => Promise<void>,
  fallback: number | number[],
): void {
  const haptics = nativeHaptics();
  if (haptics === null) {
    vibrate(fallback);
    return;
  }
  try {
    call(haptics).catch(() => {
      vibrate(fallback);
    });
  } catch {
    vibrate(fallback);
  }
}

export const haptics = {
  /** Treffer (Scan erkannt, Verkauf gebucht). */
  success(): void {
    viaPlugin((h) => h.notification({ type: 'SUCCESS' }), 40);
  },
  /** Hinweis (Warnung, z. B. bereits gescannt). */
  warning(): void {
    viaPlugin((h) => h.notification({ type: 'WARNING' }), [60, 40, 60]);
  },
  /** Fehler (unbekannter Code, abgelehnt). */
  error(): void {
    viaPlugin((h) => h.notification({ type: 'ERROR' }), [120, 60, 120]);
  },
  /** Kurzes Antippen-Gefühl (Wischschwelle erreicht). */
  tick(): void {
    viaPlugin((h) => h.impact({ style: 'LIGHT' }), 10);
  },
};
